import { forwardRef } from 'react';
import type { ComponentType } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button, ButtonProps } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

export interface ActionIconButtonProps extends Omit<ButtonProps, 'children'> {
  /** Lucide icon component to render. */
  icon: ComponentType<{ className?: string }>;
  /** Tooltip text — also used as the accessible label (aria-label). Required. */
  tooltip: string;
  /** Show the hover tooltip. Default true. */
  showTooltip?: boolean;
  /** Render with destructive coloring (red icon, red-tinted hover). */
  destructive?: boolean;
  /** Show a spinner instead of the icon (and disable) while an action is pending. */
  loading?: boolean;
}

/**
 * Generic icon-only action button with a tooltip — the canonical primitive for
 * detail-page action bars. Replaces the hand-rolled
 * `<Tooltip><TooltipTrigger asChild><Button size="icon">…` boilerplate.
 *
 * Use the semantic icon + label-key pairs from `detailActions.ts` so every page
 * uses the same icon and tooltip for the same action.
 */
export const ActionIconButton = forwardRef<HTMLButtonElement, ActionIconButtonProps>(
  (
    {
      icon: Icon,
      tooltip,
      showTooltip = true,
      destructive = false,
      loading = false,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const button = (
      <Button
        ref={ref}
        variant="ghost"
        size="icon"
        aria-label={tooltip}
        disabled={isDisabled}
        className={cn(
          'touch:min-h-[44px] touch:min-w-[44px]',
          destructive
            ? 'text-destructive hover:bg-destructive/10 hover:text-destructive'
            : 'hover:bg-accent',
          className
        )}
        {...props}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
      </Button>
    );

    if (!showTooltip) return button;

    // When disabled, wrap in a span so the tooltip still captures pointer events.
    if (isDisabled) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-block" tabIndex={0}>
              <span style={{ pointerEvents: 'none', display: 'inline-block' }}>{button}</span>
            </span>
          </TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      );
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    );
  }
);

ActionIconButton.displayName = 'ActionIconButton';
